import { Button, IconButton, MenuItem, Link, ButtonProps, LinkProps, MenuItemProps } from '@mui/material';
import { useState, ReactElement, FunctionComponent, MouseEvent, ReactNode } from 'react';

import { DangerZoneAction, DangerZoneActionProps } from '../../display';
import ProgressButton, { ProgressButtonProps } from '../ProgressButton';
import { ContentModal } from '../ContentModal';
import { CommonDialogProps } from '../types';

const COMPONENTS_MAP = {
  Button,
  IconButton,
  MenuItem,
  DangerZoneAction,
  Link,
  ProgressButton,
  fallback: Button,
};

export type BaseProps = CommonDialogProps & {
  /**
   * Callback to run *before* the dialog is shown (e.g. for lazy loading data or resetting state).
   */
  onBeforeShow?: () => void;

  /**
   * The children rendered inside the trigger component (e.g. the Button label).
   */
  children?: ReactNode;
};

type ComponentButton = ButtonProps & { componentType: 'Button' };
type ComponentIconButton = ButtonProps & { componentType: 'IconButton' };
type ComponentMenuItem = MenuItemProps & { componentType: 'MenuItem' };
type ComponentDangerZoneAction = DangerZoneActionProps & { componentType: 'DangerZoneAction' };
type ComponentLink = LinkProps & { componentType: 'Link' };
type ComponentProgressButton = ProgressButtonProps & { componentType: 'ProgressButton' };

type ButtonWithDialogProps = (
  | ComponentButton
  | ComponentIconButton
  | ComponentMenuItem
  | ComponentDangerZoneAction
  | ComponentLink
  | ComponentProgressButton
) &
  BaseProps;

export const ButtonWithDialog = ({
  dialogTitle,
  dialogContent,
  componentType = 'Button',
  confirmButtonText,
  confirmButtonProps,
  cancelButtonText = 'Cancel',
  disableConfirmButton,
  hideDialogActions = false,
  modalMaxWidth = 'sm',
  showCloseButton,
  onBeforeShow,
  children,
  ...rest
}: ButtonWithDialogProps): ReactElement => {
  const [open, setOpen] = useState(false);

  // eslint-disable-next-line
  const Trigger: FunctionComponent<any> = COMPONENTS_MAP[componentType] || COMPONENTS_MAP.fallback;

  const showDialog = () => {
    onBeforeShow?.();
    setOpen(true);
  };

  const hideDialog = () => setOpen(false);

  const handleConfirm = async (e: MouseEvent<HTMLButtonElement>) => {
    if (rest.onClick) await rest.onClick(e as any); // eslint-disable-line
    hideDialog();
  };

  return (
    <>
      <Trigger {...rest} onClick={showDialog}>
        {children}
      </Trigger>
      <ContentModal
        open={open}
        dialogTitle={dialogTitle}
        onClose={hideDialog}
        onConfirm={handleConfirm}
        confirmButtonText={confirmButtonText}
        confirmButtonProps={confirmButtonProps}
        cancelButtonText={cancelButtonText}
        disableConfirmButton={disableConfirmButton}
        modalMaxWidth={modalMaxWidth}
        hideDialogActions={hideDialogActions}
        showCloseButton={showCloseButton}
        dialogContent={typeof dialogContent === 'function' ? dialogContent(hideDialog) : dialogContent}
        fullWidth
      />
    </>
  );
};
